import { getQualifiedCredits } from "../../../../lib/helper/credit-qualification";

const normalizeKey = (key: string) => key.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

function getColumnPriceAndStrikeout({
  sel,
  miniSel,
  priceMap,
  genreOfferPrice,
  signatureOfferPrice,
  creditAmount = 0
}) {
  const genreOffers = Array.isArray(genreOfferPrice) ? genreOfferPrice : [];
  const signatureOffers = Array.isArray(signatureOfferPrice) ? signatureOfferPrice : [];
  let mainPrice = 0;
  let strikeoutPrice = 0;
  let hasStrikeout = false;
  // Signature (only one per column)
  if (sel.signature && sel.signature.length > 0) {
    const sigId = sel.signature[0];
    const normalizedSigId = normalizeKey(sigId.replace(/^ATT_TV_/, ''));
    const sigOffer = signatureOffers.find(obj => Object.keys(obj).some(key => normalizeKey(key) === normalizedSigId));
    let sigPrice = 0;
    if (sigOffer) {
      const offerKey = Object.keys(sigOffer).find(key => normalizeKey(key) === normalizedSigId);
      if (offerKey) {
        sigPrice = Number(sigOffer[offerKey]);
      }
    } else {
      const priceMapKey = normalizeKey(`ATT_TV_${sigId.replace(/^ATT_TV_/, '')}`);
      sigPrice = priceMap?.[priceMapKey] ?? 0;
    }
    mainPrice += sigPrice;
    if (sigOffer && sigOffer.strikeoutPrice && Number(sigOffer.strikeoutPrice) > sigPrice) {
      strikeoutPrice += Number(sigOffer.strikeoutPrice);
      hasStrikeout = true;
    }
  }
  const allGenreMini = [
    ...(sel.genre || []),
    ...(miniSel || [])
  ];

  let genreMiniStrikeoutSum = 0;
  let genreMiniPriceSum = 0;
  let genreMiniHasStrikeout = false;

  allGenreMini.forEach(id => {
    // Normalize genre/mini ID for lookup
    const normalizedGenreId = normalizeKey(id);

    // Find matching offer in genreOffers
    const offer = genreOffers.find(obj => Object.keys(obj).some(key => normalizeKey(key) === normalizedGenreId));

    if (offer) {
      const offerKey = Object.keys(offer).find(key => normalizeKey(key) === normalizedGenreId);
      if (offerKey && offer[offerKey]) {
        const { price, regularPrice } = offer[offerKey];
        genreMiniPriceSum += Number(price) || 0;
        if (regularPrice && Number(regularPrice) !== Number(price)) {
          genreMiniStrikeoutSum += Number(regularPrice);
          genreMiniHasStrikeout = true;
        } else {
          genreMiniStrikeoutSum += Number(price) || 0;
        }
      }
    } else {
      // Fallback: lookup price in priceMap using normalized key with prefix
      const priceMapKey = normalizeKey(`ATT_TV_${id}`);
      const price = priceMap?.[priceMapKey] ?? 0;
      genreMiniPriceSum += Number(price) || 0;
      genreMiniStrikeoutSum += Number(price) || 0;
    }
  });

  // Only show strikeout if at least one pack has an offer
  if (genreMiniHasStrikeout) {
    strikeoutPrice += genreMiniStrikeoutSum;
    hasStrikeout = true;
  }
  mainPrice += genreMiniPriceSum;

  // Apply credit to main price
  if (creditAmount > 0) {
    mainPrice = Math.max(0, mainPrice - creditAmount);
  }

  if (!hasStrikeout) {
    strikeoutPrice = 0;
  }

  return {
    mainPrice: mainPrice > 0 ? `$${mainPrice.toFixed(2)} /mo.` : "",
    strikeoutPrice: strikeoutPrice > 0 ? `$${strikeoutPrice.toFixed(2)}` : null,
    appliedCredit: creditAmount > 0 ? creditAmount : 0
  };
}

export const getPriceOffers = (accordions, priceMap, selectedPackages, columnSelections, miniSelectionsByColumn, maxColumns, genreOfferPrice, signatureOfferPrice, creditOffers?, genrePackOfferIds?, packagesConfig?) => {
  try {
    const accordionKeys = Object.keys(accordions || {});
    let finalData = [];

    // Pre-compute qualified credits for all columns once
    const creditAmountsByColumn = [];
    if (creditOffers && genrePackOfferIds && packagesConfig && columnSelections) {
      // eslint-disable-next-line no-plusplus
      for (let colIdx = 0; colIdx < maxColumns; colIdx++) {
        const sel = columnSelections[colIdx];
        if (sel && sel.genre && sel.genre.length > 0) {
          const selectedGenrePackOfferIds = {};
          sel.genre.forEach(genreId => {
            const match = packagesConfig.find(item => item.id === genreId);
            if (match) {
              const offerIds = genrePackOfferIds?.[match.queryparam];
              selectedGenrePackOfferIds[match.queryparam] = typeof offerIds === 'string' ? offerIds.split(',') : (offerIds || []);
            }
          });
          const qualifiedCredits = getQualifiedCredits(creditOffers, selectedGenrePackOfferIds);
          creditAmountsByColumn[colIdx] = qualifiedCredits.reduce((sum, credit) => sum + credit.creditAmount, 0);
        } else {
          creditAmountsByColumn[colIdx] = 0;
        }
      }
    }
    if (accordionKeys.includes('price')) {
      const priceData = {
        accordionType: "price",
        expand: accordions?.price?.expand,
        order: accordions?.price?.order,
        accordionHeading: {
          content: accordions?.price?.accordionLabel,
          color: accordions?.price?.textColor,
          backgroundColor: accordions?.price?.backgroundColor,
          tooltip: accordions?.price?.tooltip
        },
        accordionDetails: Object.values(accordions?.price?.row || {}).filter((row: any) => row != null).map((row: any) => {
          const { key, label } = row;
          const prices = selectedPackages?.map((pkg, pkgIdx) => {
            if (columnSelections && columnSelections[pkgIdx]) {
              const sel = columnSelections[pkgIdx];
              const miniSel = miniSelectionsByColumn?.[pkgIdx] || [];
              return getColumnPriceAndStrikeout({
                sel,
                miniSel,
                priceMap,
                genreOfferPrice,
                signatureOfferPrice,
                creditAmount: creditAmountsByColumn[pkgIdx] || 0
              });
            }
            // fallback
            const pkgId = pkg.id || '';
            const price = priceMap?.[normalizeKey(pkgId)] ?? 0;
            return {
              mainPrice: price > 0 ? `$${Number(price).toFixed(2)} /mo.` : "",
              strikeoutPrice: null
            };
          });

          // Pad prices array to maxColumns with "-"
          if (Array.isArray(prices) && prices.length < maxColumns) {
            const compareIdx = prices.length;
            if (columnSelections && columnSelections[compareIdx]) {
              const sel = columnSelections[compareIdx];
              const miniSel = miniSelectionsByColumn?.[compareIdx] || [];
              prices.push(getColumnPriceAndStrikeout({
                sel,
                miniSel,
                priceMap,
                genreOfferPrice,
                signatureOfferPrice,
                creditAmount: creditAmountsByColumn[compareIdx] || 0
              }));
            } else {
              prices.push({
                mainPrice: "",
                strikeoutPrice: null
              });
            }
            while (prices.length < maxColumns) {
              prices.push({
                mainPrice: "",
                strikeoutPrice: null
              });
            }
          }
          return {
            label,
            key,
            priceDetails: prices
          };
        })
      };
      finalData = [...finalData, priceData];
    }

    const orderedData = finalData.sort((a, b) => (a.order || 0) - (b.order || 0));
    return orderedData;
  } catch (_error) {
    return [];
  }
};
